// agency.js - COMPLETE VERSION WITH ALL APIS (INCLUDING TRACKING)
import { useSelector } from 'react-redux';
import { base_url } from './constants';
import axios from 'axios';

// ============================================
// 1. GLOBAL ERROR STATE MANAGEMENT
// ============================================
let globalErrorListeners = [];
let isApiDown = false;
let recoveryTimer = null;

// Event system for components to listen to API status
export const addApiStatusListener = listener => {
  globalErrorListeners.push(listener);
  return () => {
    globalErrorListeners = globalErrorListeners.filter(l => l !== listener);
  };
};

const notifyListeners = error => {
  globalErrorListeners.forEach(listener => listener(error));
};

// Health check to detect when API is back
let healthCheckInterval = null;
const startHealthCheck = () => {
  if (healthCheckInterval) clearInterval(healthCheckInterval);

  healthCheckInterval = setInterval(async () => {
    if (isApiDown) {
      try {
        // Try to hit a simple endpoint to check if API is back
        await axios.get(`${base_url}Agency/GetProfile?loginId=test`, {
          timeout: 5000,
          validateStatus: false // Don't throw on any status
        });

        // If we get here, API is responding
        console.log('✅ API is back online');
        isApiDown = false;
        notifyListeners(null); // Clear error
        clearInterval(healthCheckInterval);
        healthCheckInterval = null;

        // Auto refresh page to recover
        if (recoveryTimer) clearTimeout(recoveryTimer);
        recoveryTimer = setTimeout(() => {
          window.location.reload();
        }, 2000);
      } catch (error) {
        // Still down, continue checking
      }
    }
  }, 10000); // Check every 10 seconds
};

// ============================================
// 2. ENHANCED AXIOS INSTANCE WITH CORS HANDLING
// ============================================
const GetAuthToken = () => {
  return sessionStorage.getItem('UserAuthToken');
};

const AgencyLoginHeader = axios.create({
  baseURL: base_url,
  responseType: 'json',
  timeout: 30000 // 30 second timeout
});

// Request interceptor - blocks API calls if system is down
AgencyLoginHeader.interceptors.request.use(
  async config => {
    // If API is down, don't even make the request
    if (isApiDown) {
      const error = new Error('System is under maintenance');
      error.code = 'ERR_NETWORK';
      error.isCorsError = true;
      return Promise.reject(error);
    }

    const authToken = GetAuthToken();
    if (authToken) {
      config.headers.Authorization = `Bearer ${authToken}`;
    }
    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

// Response interceptor - catches CORS and network errors
AgencyLoginHeader.interceptors.response.use(
  response => {
    // If we get a successful response and API was down, mark it as back
    if (isApiDown) {
      isApiDown = false;
      notifyListeners(null);
      if (healthCheckInterval) {
        clearInterval(healthCheckInterval);
        healthCheckInterval = null;
      }
    }
    return response;
  },
  error => {
    let isCorsOrNetworkError = false;
    let errorMessage = '';

    // Detect CORS errors
    if (error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
      isCorsOrNetworkError = true;
      errorMessage = 'Network error - API might be updating';
    } else if (error.message && error.message.includes('CORS')) {
      isCorsOrNetworkError = true;
      errorMessage = 'CORS error - API is being updated';
    } else if (error.code === 'ECONNABORTED') {
      isCorsOrNetworkError = true;
      errorMessage = 'Request timeout - API might be down';
    } else if (error.response?.status === 503) {
      isCorsOrNetworkError = true;
      errorMessage = 'Service unavailable - API under maintenance';
    } else if (
      error.response?.status === 500 &&
      error.response?.data?.includes('CORS')
    ) {
      isCorsOrNetworkError = true;
      errorMessage = 'CORS policy error';
    }

    // Handle CORS/Network errors globally
    if (isCorsOrNetworkError && !isApiDown) {
      isApiDown = true;

      const errorObj = {
        type: 'CORS_ERROR',
        message:
          'System is currently being updated. Please wait while we restore services...',
        originalError: error,
        timestamp: new Date().toISOString()
      };

      notifyListeners(errorObj);
      startHealthCheck();
    }

    return Promise.reject(error);
  }
);

// ============================================
// 3. HELPER FUNCTION FOR WRAPPING API CALLS
// ============================================
const handleApiCall = async (apiCall, fallbackData = null) => {
  try {
    return await apiCall();
  } catch (error) {
    // Check if it's a CORS error
    if (error.code === 'ERR_NETWORK' || error.message?.includes('CORS')) {
      // Error is already handled by interceptor
      // Just re-throw for component to know
      throw error;
    }
    throw error;
  }
};

// ============================================
// 4. AUTHENTICATION APIs
// ============================================
export const AagencyLogin = async (username, pass) => {
  return await axios.post(`${base_url}Agency/SignIn`, {
    loginId: username,
    password: pass
  });
};

export const AagencyLogout = async () => {
  return await axios.post(`${base_url}Agency/SignOut`);
};

export const AagencyForgot = async email => {
  return await axios.post(`${base_url}Agency/Forgot`, {
    loginId: email
  });
};

// ============================================
// 5. PROFILE APIs
// ============================================
export const GetAagencyUserDetails = async loginId => {
  return await handleApiCall(() =>
    AgencyLoginHeader.get(`/Agency/GetProfile?loginId=${loginId}`)
  );
};

export const UpdateAgencyProfile = async UpdatedProfileObj => {
  return await handleApiCall(() =>
    AgencyLoginHeader.post(`${base_url}Agency/UpdateProfile`, UpdatedProfileObj)
  );
};

export const UploadUserProfileImage = async (agencyId, formdata) => {
  return await handleApiCall(() =>
    AgencyLoginHeader.post(
      `${base_url}Agency/UploadUserProfileImage?loginId=${agencyId}`,
      formdata
    )
  );
};

export const AgencyAdminGetProfile = async AgencyLoginID => {
  return await handleApiCall(() =>
    AgencyLoginHeader.get(
      `${base_url}Agency/GetProfile?loginId=${AgencyLoginID}`
    )
  );
};

// ============================================
// 6. USER MANAGEMENT APIs
// ============================================
export const GetUsers = async agencyId => {
  return await handleApiCall(() =>
    AgencyLoginHeader.get(`${base_url}Agency/GetUsers?agencyId=${agencyId}`)
  );
};

export const GetUserRoles = async agencyId => {
  return await handleApiCall(() =>
    AgencyLoginHeader.get(`${base_url}Agency/GetUserRoles?agencyId=${agencyId}`)
  );
};

export const CreateAgencyUser = async userObj => {
  return await handleApiCall(() =>
    AgencyLoginHeader.post(`${base_url}Agency/SignUp`, userObj)
  );
};

export const CreateAgencyRole = async obj => {
  return await handleApiCall(() =>
    AgencyLoginHeader.post(`${base_url}Agency/CreateAgencyRole`, obj)
  );
};

export const GetRolePermissions = async roleId => {
  return await handleApiCall(() =>
    AgencyLoginHeader.get(
      `${base_url}Agency/GetRolePermissions?roleId=${roleId}`
    )
  );
};

export const ResendPassword = async loginId => {
  return await handleApiCall(() =>
    AgencyLoginHeader.get(`${base_url}Agency/ResendPassword?loginId=${loginId}`)
  );
};

// ============================================
// 7. PASSWORD MANAGEMENT APIs
// ============================================
export const UpdateAgencyPassword = async (
  newPass,
  currentPass,
  UserLoggedInEmail
) => {
  return await handleApiCall(() =>
    AgencyLoginHeader.post(`${base_url}Agency/UpadatePassword`, {
      loginId: UserLoggedInEmail,
      passwordKey: '',
      password: newPass,
      currentPassword: currentPass
    })
  );
};

export const UpadateForgotPassword = async (
  confirmPassword,
  ForgotPassKey,
  LoginEmail
) => {
  return await handleApiCall(() =>
    AgencyLoginHeader.post(`${base_url}Agency/UpadateForgotPassword`, {
      loginId: LoginEmail,
      passwordKey: ForgotPassKey,
      password: confirmPassword,
      currentPassword: ''
    })
  );
};

// ============================================
// 8. SHIPMENT APIs
// ============================================
export const GetShipments = async (
  agencyId,
  loggedInEmail,
  pageNum,
  pageSize,
  searchQuery,
  shipmentStatuses,
  datesRange
) => {
  return await handleApiCall(() =>
    AgencyLoginHeader.get(
      `${base_url}Shipment/GetShipments?agencyId=${agencyId}&loginId=${loggedInEmail}&pageNumber=${pageNum}&pageSize=${pageSize}&searchQuery=${searchQuery}&shipmentStatuses=${shipmentStatuses}&fromDate=${datesRange.from}&toDate=${datesRange.to}`
    )
  );
};

export const GetBookedShipments = async agencyId => {
  return await handleApiCall(() =>
    AgencyLoginHeader.get(
      `${base_url}Shipment/GetBookedShipments?agencyId=${agencyId}`
    )
  );
};

export const GetShipmentDetails = async shipmentId => {
  return await handleApiCall(() =>
    AgencyLoginHeader.get(
      `${base_url}Shipment/GetShipmentDetails?shipmentId=${shipmentId}`
    )
  );
};

export const UpdateShipment = async shipObj => {
  return await handleApiCall(() =>
    AgencyLoginHeader.post(`${base_url}Shipment/UpdateShipment`, shipObj)
  );
};

export const CreateShipment = async obj => {
  return await handleApiCall(() =>
    AgencyLoginHeader.post(`${base_url}Shipment/CreateShipment`, obj)
  );
};

export const DispatchShipment = async obj => {
  return await handleApiCall(() =>
    AgencyLoginHeader.post(`${base_url}Shipment/DispatchShipment`, obj)
  );
};

export const HoldShipment = async obj => {
  return await handleApiCall(() =>
    AgencyLoginHeader.post(`${base_url}Shipment/HoldShipment`, obj)
  );
};

export const UnbookShipment = async obj => {
  return await handleApiCall(() =>
    AgencyLoginHeader.post(`${base_url}Shipment/UnbookShipment`, obj)
  );
};

export const CancelShipment = async obj => {
  return await handleApiCall(() =>
    AgencyLoginHeader.post(`${base_url}Shipment/CancelShipment`, obj)
  );
};

export const CancelTLShipment = async obj => {
  return await handleApiCall(() =>
    AgencyLoginHeader.post(`${base_url}Shipment/CancelTLShipment`, obj)
  );
};

export const UnHoldShipment = async obj => {
  return await handleApiCall(() =>
    AgencyLoginHeader.post(`${base_url}Shipment/UnHoldShipment`, obj)
  );
};

export const ClaimShipment = async obj => {
  return await handleApiCall(() =>
    AgencyLoginHeader.post(`${base_url}Shipment/Claim`, obj)
  );
};

export const GetShipmentClaims = async obj => {
  return await handleApiCall(() =>
    AgencyLoginHeader.get(`${base_url}Shipment/Claims`)
  );
};

export const GetClaimedShipmentDetails = async shipmentId => {
  return await handleApiCall(() =>
    AgencyLoginHeader.get(`${base_url}Shipment/Claim?shipmentId=${shipmentId}`)
  );
};

export const ImportOrder = async (agencyId, formdata) => {
  return await handleApiCall(() =>
    AgencyLoginHeader.post(
      `${base_url}Shipment/ImportOrder?agencyId=${agencyId}`,
      formdata
    )
  );
};

export const ImportInvoiceAuditShipments = async (carrierId, CsvArray) => {
  return await handleApiCall(() =>
    AgencyLoginHeader.post(
      `${base_url}Shipment/ImportInvoiceAuditShipments?carrierId=${carrierId}`,
      CsvArray
    )
  );
};

export const GetBillOfLading = async obj => {
  return await handleApiCall(() =>
    AgencyLoginHeader.post(`${base_url}Carriers/BillOfLading`, obj)
  );
};

export const GetProofOfDelivery = async obj => {
  return await handleApiCall(() =>
    AgencyLoginHeader.post(`${base_url}Carriers/ProofOfDelivery`, obj)
  );
};

export const PickupShipment = async obj => {
  return await handleApiCall(() =>
    AgencyLoginHeader.post(`${base_url}Carriers/PickupShipment`, obj)
  );
};

export const StartTLQuote = async obj => {
  return await handleApiCall(() =>
    AgencyLoginHeader.post(`${base_url}Shipment/StartTLQuote`, obj)
  );
};

export const GetQuote = async quoteId => {
  return await handleApiCall(() =>
    AgencyLoginHeader.get(`${base_url}Shipment/GetQuote?quoteId=${quoteId}`)
  );
};

export const GetQuoteDetail = async Id => {
  return await handleApiCall(() =>
    axios.get(`${base_url}Shipment/GetQuoteDetail?Id=${Id}`)
  );
};

export const BookTLShipment = async bookTLShipmentObj => {
  return await handleApiCall(() =>
    AgencyLoginHeader.post(
      `${base_url}Shipment/BookTLShipment`,
      bookTLShipmentObj
    )
  );
};

export const UpdateTLShipment = async UpdateTLShipmentObj => {
  return await handleApiCall(() =>
    axios.post(`${base_url}Shipment/UpdateTLShipment`, UpdateTLShipmentObj)
  );
};

export const ResendQuote = async Id => {
  return await handleApiCall(() =>
    AgencyLoginHeader.get(`${base_url}Shipment/ResendQuote?Id=${Id}`, Id)
  );
};

export const AddMoreRateRequest = async Id => {
  return await handleApiCall(() =>
    axios.post(`${base_url}Shipment/AddMoreRate?Id=${Id}`)
  );
};

export const GetShipmentActivity = async shipmentId => {
  return await handleApiCall(() =>
    AgencyLoginHeader.get(
      `${base_url}Shipment/GetShipmentActivity?shipmentId=${shipmentId}`
    )
  );
};

export const InvoiceAuditLogs = async shipmentId => {
  return await handleApiCall(() =>
    AgencyLoginHeader.get(
      `${base_url}Shipment/InvoiceAuditLogs?shipmentId=${shipmentId}`
    )
  );
};

export const UpdateShipmentContacts = async obj => {
  return await handleApiCall(() =>
    AgencyLoginHeader.post(`${base_url}Shipment/UpdateShipmentContacts`, obj)
  );
};

export const UpdateShipmentDetail = async obj => {
  return await handleApiCall(() =>
    AgencyLoginHeader.post(`${base_url}Shipment/UpdateShipmentDetail`, obj)
  );
};

export const SendDisputedInvoiceReport = async InvoiceReportObj => {
  return await handleApiCall(() =>
    AgencyLoginHeader.post(
      `${base_url}Shipment/SendDisputedInvoiceReport`,
      InvoiceReportObj
    )
  );
};

export const UpdateShipmentLocation = async obj => {
  return await handleApiCall(() =>
    AgencyLoginHeader.post(`${base_url}Shipment/UpdateShipmentLocation`, obj)
  );
};

export const CheckLocation = async shipmentId => {
  return await handleApiCall(() =>
    AgencyLoginHeader.get(
      `${base_url}Shipment/CheckLocation?shipmentId=${shipmentId}`
    )
  );
};

export const GetNextDate = async (startDate, days, dateFormat) => {
  return await handleApiCall(() =>
    AgencyLoginHeader.get(
      `${base_url}Shipment/GetNextDate?startDate=${startDate}&days=${days}&outputFormat=${dateFormat}`
    )
  );
};

export const GetShipmentById = async shipmentId => {
  return await handleApiCall(() =>
    AgencyLoginHeader.get(
      `${base_url}Shipment/GetShipmentById?shipmentId=${shipmentId}`
    )
  );
};

// ============================================
// 9. TRACKING APIs (COMPLETE - ALL TRACKING FUNCTIONS)
// ============================================

// Get Track Shipment by reference number
export const GetTrackShipment = async reffNO => {
  return await handleApiCall(() =>
    AgencyLoginHeader.get(
      `${base_url}Shipment/GetShipment?orderNumber=${reffNO}`
    )
  );
};

// Track Shipment Request
export const TrackShipmentRequest = async obj => {
  return await handleApiCall(() =>
    AgencyLoginHeader.post(`${base_url}Carriers/TrackShipment`, obj)
  );
};

// TL Shipment History
export const TLShipmentHistory = async (carrierId, fromZip, toZip) => {
  return await handleApiCall(() =>
    AgencyLoginHeader.get(
      `${base_url}Shipment/TLShipmentHistory?carrierId=${carrierId}&fromZip=${fromZip}&toZip=${toZip}`
    )
  );
};

// Check Tracking Status (Auto Service Jobs)
export const CheckTrackingStatus = async () => {
  return await AgencyLoginHeader.get(`${base_url}Jobs/CheckTrackingStatus`);
};

// ============================================
// 10. CARRIER APIs
// ============================================
export const GetCarriers = async (agencyId, locationId) => {
  return await handleApiCall(() =>
    AgencyLoginHeader.get(
      `${base_url}Agency/GetAllCarriers?agencyId=${agencyId}&locationId=${locationId ? locationId : 0}`
    )
  );
};

export const GetActiveCarriers = async (agencyId, locationId, signal) => {
  return AgencyLoginHeader.get(
    `${base_url}Agency/GetActiveCarriers?agencyId=${agencyId}&locationId=${locationId ? locationId : 0}`,
    { signal }
  );
};

export const GetCarrierDetails = async (agencyId, carrierId, locationID) => {
  return await handleApiCall(() =>
    AgencyLoginHeader.get(
      `${base_url}Agency/GetCarrierDetails?agencyId=${agencyId}&carrierId=${carrierId}&locationId=${locationID}`
    )
  );
};

export const UpdateCarrierDetails = async carriersArray => {
  return await handleApiCall(() =>
    AgencyLoginHeader.post(
      `${base_url}Agency/UpdateCarrierDetails`,
      carriersArray
    )
  );
};

export const UpdateCarrierBillTo = async data => {
  return await handleApiCall(() =>
    AgencyLoginHeader.post(`${base_url}Agency/UpdateCarrierBillTo`, data)
  );
};

export const UpdateCarrierClaimSettings = async obj => {
  return await handleApiCall(() =>
    AgencyLoginHeader.post(`${base_url}Agency/UpdateCarrierClaimSettings`, obj)
  );
};

export const UpdateCarrierRateQuoteSettings = async obj => {
  return await handleApiCall(() =>
    AgencyLoginHeader.post(
      `${base_url}Agency/UpdateCarrierRateQuoteSettings`,
      obj
    )
  );
};

export const UpdateCarrierSurcharge = async obj => {
  return await handleApiCall(() =>
    AgencyLoginHeader.post(`${base_url}Agency/UpdateCarrierSurcharge`, obj)
  );
};

export const UpdateCarrierProSeries = async obj => {
  return await handleApiCall(() =>
    AgencyLoginHeader.post(`${base_url}Agency/UpdateCarrierProSeries`, obj)
  );
};

export const UpdateCarrierThirdPartyInfo = async obj => {
  return await handleApiCall(() =>
    AgencyLoginHeader.post(`${base_url}Agency/UpdateCarrierThirdPartyInfo`, obj)
  );
};

export const CreateOrUpdateCarrier = async obj => {
  return await handleApiCall(() =>
    AgencyLoginHeader.post(`${base_url}Agency/CreateOrUpdateCarrier`, obj)
  );
};

export const GetCarrierEProSeries = async carrierId => {
  return await handleApiCall(() =>
    AgencyLoginHeader.get(
      `${base_url}Agency/CarrierEProSeries?carrierId=${carrierId}`
    )
  );
};

export const GenerateCarrierEProSeries = async dataObj => {
  return await handleApiCall(() =>
    AgencyLoginHeader.post(`${base_url}Carriers/GenerateProSeries`, dataObj)
  );
};

export const ImportCarrierEProSeries = async dataObj => {
  return await handleApiCall(() =>
    AgencyLoginHeader.post(`${base_url}Agency/ImportCarrierEProSeries`, dataObj)
  );
};

export const GetNextProNumber = async (agencyID, carrierId) => {
  return await handleApiCall(() =>
    AgencyLoginHeader.get(
      `${base_url}Carriers/GetNextProNumber?agencyId=${agencyID}&carrierId=${carrierId}`
    )
  );
};

export const UpdateCarrierInvoiceAuditThreshold = async obj => {
  return await handleApiCall(() =>
    AgencyLoginHeader.post(
      `${base_url}Agency/UpdateCarrierInvoiceAuditThreshold`,
      obj
    )
  );
};

export const GetCarrierImportCol = async carrierId => {
  return await handleApiCall(() =>
    AgencyLoginHeader.get(`${base_url}Agency/GetCarrier?carrierId=${carrierId}`)
  );
};

export const GetCarrierProSeriesStatus = async (agencyId, locationId) => {
  return await handleApiCall(() =>
    AgencyLoginHeader.get(`${base_url}Agency/GetCarrierProSeriesStats`)
  );
};

export const MarkAsResolvedDisputedShipment = async shipId => {
  return await handleApiCall(() =>
    AgencyLoginHeader.get(
      `${base_url}Agency/MarkAsResolvedDisputedShipment?id=${shipId}`
    )
  );
};

export const GetCarrierLocations = async (agencyId, carrierId) => {
  return await handleApiCall(() =>
    AgencyLoginHeader.get(
      `${base_url}Agency/GetCarrierLocations?agencyId=${agencyId}&carrierId=${carrierId}`
    )
  );
};

export const UploadCarrierLogo = async (agencyId, formData) => {
  return await handleApiCall(() =>
    AgencyLoginHeader.post(
      `${base_url}Agency/UploadCarrierLogo?agencyId=${agencyId}`,
      formData
    )
  );
};

// ============================================
// 11. LOCATION APIs
// ============================================
export const GetLocations = async agencyId => {
  return await handleApiCall(() =>
    AgencyLoginHeader.get(`${base_url}Agency/GetLocations?agencyId=${agencyId}`)
  );
};

export const GetLocationCarriers = async (agencyID, locationID) => {
  return await handleApiCall(() =>
    AgencyLoginHeader.get(
      `${base_url}Agency/GetLocationCarriers?agencyId=${agencyID}&locationId=${locationID}`
    )
  );
};

export const AddUpdateLocation = async locationObj => {
  return await handleApiCall(() =>
    AgencyLoginHeader.post(`${base_url}Agency/AddUpdateLocation`, locationObj)
  );
};

export const AddUpdateLocationCarrier = async locationCarrierObj => {
  return await handleApiCall(() =>
    AgencyLoginHeader.post(
      `${base_url}Agency/AddUpdateLocationCarrier`,
      locationCarrierObj
    )
  );
};

export const GetAgencyLocations = async (agencyID, locationId) => {
  return await handleApiCall(() =>
    AgencyLoginHeader.get(
      `${base_url}Agency/GetAgencyLocations?agencyId=${agencyID}&locationId=${locationId}`
    )
  );
};

export const SetAsDefaultLocation = async locationId => {
  return await handleApiCall(() =>
    AgencyLoginHeader.post(
      `${base_url}Agency/SetAsDefaultLocation?locationId=${locationId}`
    )
  );
};

export const UpdateLocationCarrier = async obj => {
  return await handleApiCall(() =>
    AgencyLoginHeader.post(`${base_url}Agency/UpdateLocationCarrier`, obj)
  );
};

export const GetLocationEProSeries = async (locationId, carrierId) => {
  return await handleApiCall(() =>
    AgencyLoginHeader.get(
      `${base_url}Agency/CarrierEProSeries?carrierId=${carrierId}&locationId=${locationId}`
    )
  );
};

// ============================================
// 12. NOTIFICATION APIs
// ============================================
export const GetAppNotifications = async (unread, records) => {
  return await handleApiCall(() =>
    AgencyLoginHeader.get(
      `${base_url}Agency/AppNotifications?unread=${unread}&records=${records}`
    )
  );
};

export const AppNotificationMarkAsRead = async nid => {
  return await handleApiCall(() =>
    AgencyLoginHeader.post(
      `${base_url}Agency/AppNotificationMarkAsRead?notificationId=${nid}`
    )
  );
};

export const SaveNotificationSettings = async data => {
  return await handleApiCall(() =>
    AgencyLoginHeader.post(`${base_url}Agency/SaveNotificationSettings`, data)
  );
};

export const GetNotificationSettings = async () => {
  return await handleApiCall(() =>
    AgencyLoginHeader.get(`${base_url}Agency/NotificationSettings`)
  );
};

export const MarkAsReadMessages = async idsArray => {
  return await handleApiCall(() =>
    AgencyLoginHeader.post(`${base_url}Shipment/MarkAsReadMessages`, idsArray)
  );
};

// ============================================
// 13. SETTINGS APIs
// ============================================
export const GetGeneralSettings = async () => {
  return await handleApiCall(() =>
    AgencyLoginHeader.get(`${base_url}Agency/GetSettings`)
  );
};

export const UpdateGeneralSettings = async data => {
  return await handleApiCall(() =>
    AgencyLoginHeader.post(`${base_url}Agency/UpdateSettings`, data)
  );
};

export const SaveUserSettings = async obj => {
  return await handleApiCall(() =>
    AgencyLoginHeader.post(`${base_url}Agency/SaveUserSettings`, obj)
  );
};

// ============================================
// 14. FILE UPLOAD APIs
// ============================================
export const UploadFileAPI = async (destination, file) => {
  return await handleApiCall(() =>
    AgencyLoginHeader.post(
      `${base_url}Shipment/UploadFile?destination=${destination}`,
      file
    )
  );
};

export const GetLogoBase64 = async filePath => {
  return await handleApiCall(() =>
    AgencyLoginHeader.get(
      `${base_url}Agency/GetLogoBase64?filePath=${filePath}`
    )
  );
};

// ============================================
// 15. IMPORT/EXPORT APIs
// ============================================
export const GetImportFieldNames = async UpdatedProfileObj => {
  return await handleApiCall(() =>
    AgencyLoginHeader.get(`${base_url}Shipment/GetImportFieldNames`, {
      UpdatedProfileObj
    })
  );
};

export const ImportCSVMapping = async (
  CsvArray,
  AgencyID,
  UserLoggedInEmail
) => {
  return await handleApiCall(() =>
    AgencyLoginHeader.post(
      `${base_url}Shipment/ImportCSVMapping?agencyId=${AgencyID}&loginId=${UserLoggedInEmail}`,
      CsvArray
    )
  );
};

export const UpdateIAColumnMapping = async obj => {
  return await handleApiCall(() =>
    AgencyLoginHeader.post(`${base_url}Agency/UpdateIAColumnMapping`, obj)
  );
};

// ============================================
// 16. APP REGISTRATION APIs
// ============================================
export const SaveAppRegistration = async appRegistrationObj => {
  return await handleApiCall(() =>
    AgencyLoginHeader.post(
      `${base_url}Agency/SaveAppRegistration`,
      appRegistrationObj
    )
  );
};

export const GetAppRegistrations = async agencyID => {
  return await handleApiCall(() =>
    AgencyLoginHeader.get(
      `${base_url}Agency/GetAppRegistrations?agencyId=${agencyID}`
    )
  );
};

// ============================================
// 17. CHAT/LOG APIs
// ============================================
export const LogTLCarrierResponse = async chatObj => {
  return await handleApiCall(() =>
    axios.post(`${base_url}Shipment/LogTLCarrierResponse`, chatObj)
  );
};

export const LogTLCarrierReScheduleResponse = async rescheduleObj => {
  return await handleApiCall(() =>
    axios.post(
      `${base_url}Shipment/LogTLCarrierReScheduleResponse`,
      rescheduleObj
    )
  );
};

export const LogTLCarrierReQuoteResponse = async chatObj => {
  return await handleApiCall(() =>
    axios.post(`${base_url}Shipment/LogTLCarrierReQuoteResponse`, chatObj)
  );
};

export const LogTLShipperResponse = async chatObj => {
  return await handleApiCall(() =>
    AgencyLoginHeader.post(`${base_url}Shipment/LogTLShipperResponse`, chatObj)
  );
};

export const FailedLogs = async obj => {
  return await handleApiCall(() =>
    AgencyLoginHeader.get(`${base_url}Agency/FailedLogs`, obj)
  );
};

export const MarkAsResolvedLog = async id => {
  return await handleApiCall(() =>
    AgencyLoginHeader.get(`${base_url}Agency/MarkAsResolvedLog?id=${id}`)
  );
};

// ============================================
// 18. GOOGLE/MAP APIs
// ============================================
export const GoogleMapApi = async squery => {
  return await axios.get(
    `https://google-autocomplete-api.onrender.com/api/GoogleMapApi?input=${squery}`
  );
};

export const GoogleAddressDetailsApi = async firstPlaceId => {
  return await axios.get(
    `https://google-autocomplete-api.onrender.com/api/GoogleAddressDetailsApi/?place_id=${firstPlaceId}`
  );
};

export const GetLocationsJSON = async squery => {
  return await axios.get(`${base_url}Carriers/SearchLocation?query=${squery}`);
};

export const AddLocation = async obj => {
  return await AgencyLoginHeader.post(`${base_url}Carriers/AddLocation`, obj);
};

export const CalculateDistance = async (from, to) => {
  return await AgencyLoginHeader.get(
    `${base_url}Carriers/CalculateDistance?from=${from}&to=${to}`
  );
};

// ============================================
// 19. DASHBOARD APIs
// ============================================
export const EmbedDashboard = async () => {
  return await AgencyLoginHeader.get(`${base_url}Dashboard/EmbedDashboard`);
};

// ============================================
// 20. ADMIN APIs
// ============================================
export const GetAllCarriers = async type => {
  return await handleApiCall(() =>
    AgencyLoginHeader.get(`${base_url}Admin/GetCarriers?carrierType=${type}`)
  );
};

// ============================================
// 21. APPS APIs
// ============================================
export const Authenticate = async obj => {
  return await axios.post(`${base_url}Apps/Authenticate`, obj);
};

export const QuoteAndSchedule = async (obj, token) => {
  return await axios.post(`${base_url}Apps/QuoteAndSchedule`, obj, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  });
};

export const Pickup = async (obj, token) => {
  return await axios.post(`${base_url}Apps/Pickup`, obj, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  });
};

// ============================================
// 22. EXPORT UTILITIES FOR COMPONENTS
// ============================================
export const isApiCurrentlyDown = () => isApiDown;
export const forceHealthCheck = () => startHealthCheck();
